/* component-yotpo-reviews.js
   Behaviour and text touches on the Yotpo Reviews widget that CSS cannot make
   (component-yotpo-reviews.css owns layout and type). Two text rewrites, both
   idempotent so the widget's own re-renders (sort, filter, page) and our own writes
   never loop, plus an appending desktop Load more (3, below):
     1. bottom line "Based on 39 reviews" -> "39 Reviews" (Figma 4234:345255) unless
        the Yotpo admin's BottomLine text already reads that way;
     2. review dates DD/MM/YY (the admin default) -> relative "2 months ago"
        (Figma 10160:51846) via Intl.RelativeTimeFormat, original kept on a title
        attribute; an unparseable date is left alone.
   The widget mounts late and is a Vue app, so a body-level observer waits for it and
   a subtree observer follows its re-renders, coalesced to one pass per frame. */
(function () {
  var ROOT_ID = 'yotpo-reviews-main-widget';
  var lang = (document.documentElement.lang || 'en').split('-')[0];
  var rtf = null;
  try {
    rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  } catch (error) {
    rtf = null;
  }

  function relative(date) {
    if (!rtf) return null;
    var days = Math.round((date.getTime() - Date.now()) / 86400000);
    var abs = Math.abs(days);
    if (abs < 1) return rtf.format(0, 'day');
    if (abs < 7) return rtf.format(days, 'day');
    if (abs < 30) return rtf.format(Math.round(days / 7), 'week');
    if (abs < 365) return rtf.format(Math.round(days / 30.4375), 'month');
    return rtf.format(Math.round(days / 365.25), 'year');
  }

  /* DD/MM/YY or DD/MM/YYYY, as the Yotpo admin's default date format prints. */
  function parseDate(text) {
    var m = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\s*$/.exec(text);
    if (!m) return null;
    var year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    var date = new Date(year, Number(m[2]) - 1, Number(m[1]));
    return isNaN(date.getTime()) ? null : date;
  }

  function fixDates(root) {
    root.querySelectorAll('.yotpo-date-format').forEach(function (el) {
      var raw = el.getAttribute('data-billini-date') || el.textContent;
      if (el.getAttribute('data-billini-date') === raw && el.getAttribute('data-billini-relative') === el.textContent) return;
      var date = parseDate(raw);
      if (!date) return;
      var label = relative(date);
      if (!label) return;
      el.setAttribute('data-billini-date', raw.trim());
      el.setAttribute('title', raw.trim());
      if (el.textContent !== label) el.textContent = label;
      el.setAttribute('data-billini-relative', label);
    });
  }

  function fixBottomLine(root) {
    var el = root.querySelector('.yotpo-bottom-line-basic-text');
    if (!el) return;
    var text = el.textContent.trim();
    if (/^\d[\d,]*\s+Reviews?$/.test(text)) return;
    var m = /(\d[\d,]*)/.exec(text);
    if (!m) return;
    var n = Number(m[1].replace(/,/g, ''));
    var label = m[1] + (n === 1 ? ' Review' : ' Reviews');
    if (el.textContent !== label) el.textContent = label;
  }

  /* Productive #247 (client review): below Yotpo's breakpoint the rating dropdown
     becomes a "Filters" button that opens the same rating filter in a popup. The
     client wants it to read like the desktop control, so the label is rewritten from
     the theme's translation (exposed on window.theme by layout/theme.liquid). Skipped
     when the string is absent or unresolved, so the widget's own label stands in. */
  function fixFilterButton(root) {
    var label = window.theme && window.theme.reviewsFilterLabel;
    if (!label || /translation missing/i.test(label)) return;
    root.querySelectorAll('.yotpo-mobile-filters-btn-text').forEach(function (el) {
      if (el.textContent !== label) el.textContent = label;
    });
  }

  function pass(root) {
    reconcileKept(root);
    fixBottomLine(root);
    fixDates(root);
    fixFilterButton(root);
  }

  /* 3. Desktop "Load more" APPENDS. Yotpo's desktop pager (.yotpo-horizontal-pagination,
        styled by the CSS as a single full-width Load more) swaps the page of reviews in
        place; its mobile pager already appends. To make desktop behave like a load more,
        the reviews on screen are cloned before Yotpo swaps them and put back above the
        new page once it lands, so the list grows and nothing on screen moves. Clones are
        inert copies (a Read-more toggle or media lightbox on an OLDER review no longer
        responds) - an accepted trade for text reviews. Vue only patches its own nodes, so
        the clones survive a sort or filter change; pass() purges them whenever Yotpo
        re-renders the list for any reason other than our pending Load more. */
  var pendingAppend = false;
  var anchorText = null;

  function firstOwnReview(root) {
    return root.querySelector('.yotpo-reviews-list .yotpo-review:not([data-billini-kept])');
  }

  function purgeKept(root) {
    root.querySelectorAll('.yotpo-reviews-list [data-billini-kept]').forEach(function (n) {
      n.remove();
    });
    anchorText = null;
  }

  function reconcileKept(root) {
    if (pendingAppend || anchorText === null) return;
    var first = firstOwnReview(root);
    if (!first || first.innerText !== anchorText) purgeKept(root);
  }

  function appendingLoadMore(root) {
    root.addEventListener(
      'click',
      function (event) {
        var link = event.target.closest('.yotpo-horizontal-pagination > a:last-child');
        if (!link || link.getAttribute('aria-disabled') === 'true' || pendingAppend) return;
        var list = root.querySelector('.yotpo-reviews-list');
        var first = firstOwnReview(root);
        if (!list || !first) return;
        var marker = first.innerText;
        var kept = Array.prototype.filter
          .call(list.children, function (el) {
            return !el.classList.contains('yotpo-reviews-pagination-container');
          })
          .map(function (el) {
            var clone = el.cloneNode(true);
            clone.removeAttribute('id');
            clone.querySelectorAll('[id]').forEach(function (n) {
              n.removeAttribute('id');
            });
            clone.setAttribute('data-billini-kept', '');
            return clone;
          });
        pendingAppend = true;
        var tries = 0;
        (function waitForNewPage() {
          var nextList = root.querySelector('.yotpo-reviews-list');
          var nextFirst = firstOwnReview(root);
          if (nextList && nextFirst && nextFirst.innerText !== marker) {
            /* Yotpo has swapped the page: drop the clones it left behind from earlier
               appends (they are inside `kept` already), then put the kept set back on top. */
            nextList.querySelectorAll('[data-billini-kept]').forEach(function (n) {
              n.remove();
            });
            var fragment = document.createDocumentFragment();
            kept.forEach(function (n) {
              fragment.appendChild(n);
            });
            nextList.insertBefore(fragment, nextList.firstChild);
            anchorText = nextFirst.innerText;
            pendingAppend = false;
            return;
          }
          if (++tries < 80) setTimeout(waitForNewPage, 100);
          else pendingAppend = false;
        })();
      },
      true,
    );
  }

  function attach(root) {
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        pass(root);
      });
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    pass(root);
    appendingLoadMore(root);
  }

  function boot() {
    var root = document.getElementById(ROOT_ID);
    if (root) {
      attach(root);
      return;
    }
    var waiter = new MutationObserver(function () {
      var found = document.getElementById(ROOT_ID);
      if (!found) return;
      waiter.disconnect();
      attach(found);
    });
    waiter.observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
